/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';

import Paper from '@mui/material/Paper';

import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import UploadFile from './UploadFile';
import UploadLiveMeeting from './UploadLiveMeeting';
import UploadUrl from './UploadUrl';
import { useTranscriptUploadActions } from '../../actions/FileActions';
import { useFileUploadActions } from '../../actions/FileActions';
import { useRateLimitsQuery } from '../../actions/WorkspaceActions';
import UploadRecord from './UploadRecord';
import UploadCalendar from './UploadCalendar';
import UploadPhone from './UploadPhone';
import { useUserQuery } from '../../actions/UserActions';

import AppContext from '../../contexts/app';
import SkeletonContext from '../../contexts/SkeletonContext';

import { track } from '../../utils/track';
import { confirmDialog } from '../../utils/dialog';
import { IS_SINGLE_TENENT_MODE } from '../../constants';

export default function UploadDialog({ large = false, largeText = false }) {
  const { t } = useTranslation();
  const { fetchUser, ...rest } = useContext(SkeletonContext);
  const { user } = useUserQuery({ enabled: fetchUser });
  const { uploadDialog, setUploadDialog } = useContext(AppContext);
  const { open, type: uploadType } = uploadDialog || {};
  const { uploadTranscriptAsync, cancelUpload, uploadError } =
    useTranscriptUploadActions();
  const { uploadFileAsync } = useFileUploadActions();
  const { rateLimits } = useRateLimitsQuery({
    enabled: !!user?.role && user?.role !== 'guest' && !!user?.workspace,
  });

  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    track('upload_modal', { open, uploadType });
    if (open) {
      track('upload_modal_open', { uploadType });
    }
  }, [open, uploadType]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setUploadDialog({ open: false, type: null });
      }, 250);
    }
  }, [open, setUploadDialog]);

  useEffect(() => {
    function handleUnload(e) {
      const msg = t(
        'Du har en fil som inte är uppladdad. Är du säker på att du vill lämna sidan?'
      );
      e.preventDefault();
      (e || window.event).returnValue = msg;
      return msg;
    }
    if (uploading) {
      window.addEventListener('beforeunload', handleUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [uploading]);

  async function handleClose() {
    if (uploading) {
      const ok = await confirmDialog({
        title: t('Avbryta uppladdning?'),
        content: t('Är du säker på att du vill avbryta uppladdningen?'),
      });
      if (!ok) return;
    }
    cancelUpload();
    setUploadDialog({ open: false, type: uploadType });
  }

  const exceedsUploadLimit =
    user?.workspace?.type === 'trial' && rateLimits?.upload?.exceeded;

  const size = uploadType === 'file' ? 'sm' : 'xs';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={size}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
        },
      }}
    >
      <Paper
        css={css`
          padding: 1rem;
        `}
      >
        {open ? (
          <>
            {uploadType === 'url' ? (
              <UploadUrl
                handleClose={handleClose}
                exceedsUploadLimit={exceedsUploadLimit}
              />
            ) : null}
            {uploadType === 'file' ? (
              <UploadFile
                handleClose={handleClose}
                setUploading={setUploading}
                uploadFile={uploadFileAsync}
                uploadTranscript={uploadTranscriptAsync}
                uploadError={uploadError}
                exceedsUploadLimit={exceedsUploadLimit}
                uploadDialog={uploadDialog}
              />
            ) : null}
            {uploadType === 'liveMeeting' ? (
              <UploadLiveMeeting handleClose={handleClose} />
            ) : null}
            {uploadType === 'record' ? (
              <UploadRecord
                handleClose={handleClose}
                setUploading={setUploading}
              />
            ) : null}
            {uploadType === 'calendar' ? (
              <UploadCalendar handleClose={handleClose} />
            ) : null}
            {uploadType === 'phone' ? (
              <UploadPhone
                handleClose={handleClose}
                setUploading={setUploading}
              />
            ) : null}
          </>
        ) : null}
        {!uploadType ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            <Button
              css={css`
                height: 48px;
              `}
              onClick={() => setUploadDialog({ open: true, type: 'file' })}
              variant="outlined"
              fullWidth
            >
              <UploadOutlinedIcon sx={{ marginRight: '10px' }} />
              {t('Ladda upp fil')}
            </Button>
            <Button
              css={css`
                height: 48px;
              `}
              onClick={() => setUploadDialog({ open: true, type: 'record' })}
              variant="outlined"
              fullWidth
            >
              <MicNoneOutlinedIcon sx={{ marginRight: '10px' }} />
              {t('Diktafon')}
            </Button>
            {!IS_SINGLE_TENENT_MODE && (
              <React.Fragment>
                <Button
                  css={css`
                    height: 48px;
                  `}
                  onClick={() =>
                    setUploadDialog({ open: true, type: 'liveMeeting' })
                  }
                  variant="outlined"
                  fullWidth
                >
                  <VideocamOutlinedIcon sx={{ marginRight: '10px' }} />
                  {t('Spela in webbmöte')}
                </Button>
                <Button
                  css={css`
                    height: 48px;
                  `}
                  onClick={() =>
                    setUploadDialog({ open: true, type: 'calendar' })
                  }
                  variant="outlined"
                  fullWidth
                >
                  <CalendarTodayOutlinedIcon sx={{ marginRight: '10px' }} />
                  {t('Kalendarinbjudan')}
                </Button>
                <Button
                  css={css`
                    height: 48px;
                  `}
                  onClick={() => setUploadDialog({ open: true, type: 'phone' })}
                  variant="outlined"
                  fullWidth
                >
                  <PhoneOutlinedIcon sx={{ marginRight: '10px' }} />
                  {t('Spela in telefonsamtal')}
                </Button>
                <Button
                  css={css`
                    height: 48px;
                  `}
                  onClick={() => setUploadDialog({ open: true, type: 'url' })}
                  variant="outlined"
                  fullWidth
                >
                  <LinkOutlinedIcon sx={{ marginRight: '10px' }} />
                  {t('Importera URL')}
                </Button>
              </React.Fragment>
            )}
          </div>
        ) : null}
      </Paper>
    </Dialog>
  );
}
